/**
 * MonthUtilizationStrip
 * ──────────────────────────────────────────────────────────────────
 * Compact read-only strip that fetches `/api/scheduler/slots/calendar`
 * for the visible month and surfaces utilization stats (total open
 * slots, booked slots, overall %) above the day grid.
 *
 * The endpoint already exists — vendor §M9c. NO new API shape
 * invented. Renders safely under partial data (zero days, missing
 * `slots[]`, server error). Errors don't take the calendar offline;
 * they collapse this strip silently.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import * as schedulerApi from '../../../../lib/api/schedulerApi';
import { Loader2, AlertCircle } from 'lucide-react';

const isoDate = (d) => d.toISOString().slice(0, 10);
const monthRange = (anchor) => {
  // Monday-rooted month range with safe clamps for partial month boundaries.
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return { start: isoDate(start), end: isoDate(end) };
};

const MonthUtilizationStrip = ({ locationId, anchorDate }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const range = useMemo(() => monthRange(anchorDate || new Date()), [anchorDate]);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { start_date: range.start, end_date: range.end };
    if (locationId) params.location_id = locationId;
    const res = await schedulerApi.queryAvailabilityCalendar(params);
    setLoading(false);
    if (!res?.ok) {
      setError(res?.error || { message: 'Calendar unavailable' });
      setDays([]);
      return;
    }
    setDays(Array.isArray(res.data?.days) ? res.data.days : []);
  }, [range.start, range.end, locationId]);

  useEffect(() => { refetch(); }, [refetch]);

  /* Aggregate totals across the response. Render safely when slots[]
   * is missing or any day is partial. */
  const totals = useMemo(() => {
    let total = 0;
    let open = 0;
    let booked = 0;
    for (const d of (days || [])) {
      const slots = Array.isArray(d?.slots) ? d.slots : [];
      for (const s of slots) {
        total += 1;
        if (s?.available === true) open += 1;
        else booked += 1;
      }
    }
    const utilizationPct = total > 0 ? Math.round((booked / total) * 100) : null;
    return { total, open, booked, utilizationPct, dayCount: days?.length || 0 };
  }, [days]);

  if (error && error.status !== 403 && error.status !== 404) {
    return (
      <div
        className="px-3 py-1.5 mb-2 rounded-md border border-rose-200 bg-rose-50/40 text-[11px] text-rose-700 flex items-center gap-1.5"
        data-testid="month-utilization-error"
      >
        <AlertCircle className="h-3 w-3" />
        Calendar overview unavailable — {error.message || 'request failed'}.
      </div>
    );
  }

  // Soft-collapse on 403/404 — avoid noisy alerts on tenants without
  // the route provisioned. The day grid below stays visible.
  if (error) return null;

  if (loading && totals.total === 0) {
    return (
      <div
        className="px-3 py-1.5 mb-2 rounded-md border border-slate-200 bg-slate-50/40 text-[11px] text-slate-500 flex items-center gap-1.5"
        data-testid="month-utilization-loading"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading month overview…
      </div>
    );
  }

  if (totals.total === 0) {
    return (
      <div
        className="px-3 py-1.5 mb-2 rounded-md border border-slate-200 bg-slate-50/40 text-[11px] text-slate-500"
        data-testid="month-utilization-empty"
      >
        No slot data published for {range.start.slice(0, 7)} yet.
      </div>
    );
  }

  return (
    <div
      className="px-3 py-1.5 mb-2 rounded-md border border-slate-200 bg-white text-[11px] text-slate-600 flex items-center gap-3"
      data-testid="month-utilization-strip"
    >
      <span className="font-semibold text-slate-700">
        {range.start.slice(0, 7)}
      </span>
      <span data-testid="mu-utilization">
        Utilization: <span className="font-medium text-slate-900">{totals.utilizationPct ?? '—'}%</span>
      </span>
      <span className="text-slate-400">·</span>
      <span data-testid="mu-booked">
        Booked: <span className="font-medium text-slate-900">{totals.booked}</span>
      </span>
      <span className="text-slate-400">·</span>
      <span data-testid="mu-open">
        Open: <span className="font-medium text-slate-900">{totals.open}</span>
      </span>
      <span className="text-slate-400">·</span>
      <span className="text-slate-500">{totals.dayCount} days</span>
    </div>
  );
};

export default MonthUtilizationStrip;
